import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  BookOpen, Users, Package, ShoppingCart, Percent, TrendingUp, 
  Settings, Clock, Shield, AlertCircle, CheckCircle, Info,
  FileText, Upload, Edit, Trash2, Plus, Search,
  DollarSign, Utensils, BarChart3, Mail, Phone, Calendar,
  CreditCard, Hash, Activity, Filter, Eye, UserCheck, Tag,
  Leaf, Snowflake, Flame, Archive, ArrowRight, Star
} from 'lucide-react';
import { Link } from 'wouter';
import { AdminNavigation } from '@/components/AdminNavigation';

export default function AdminGuide() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50 print:bg-white">
      <AdminNavigation />

      {/* Header */}
      <div className="bg-white shadow-sm border-b print:shadow-none">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-emerald-600" />
              <h1 className="text-2xl font-bold">Administrator Guide</h1>
            </div>
            <Button onClick={handlePrint} variant="outline" className="print:hidden">
              <FileText className="w-4 h-4 mr-2" />
              Print Guide
            </Button>
          </div>
        </div>
      </div>

      {/* Guide Content */}
      <div className="container mx-auto p-4 max-w-4xl space-y-8">
        {/* Title Page */}
        <Card className="print:break-before-page print:shadow-none print:border-2 text-center py-16">
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <BookOpen className="w-24 h-24 mx-auto text-emerald-600" />
              <h1 className="text-4xl font-bold text-gray-900">
                Tops Supermarket
              </h1>
              <h2 className="text-3xl font-semibold text-emerald-600">
                Management System
              </h2>
              <h3 className="text-2xl text-gray-700">
                Administrator Guide
              </h3>
            </div>
            
            <div className="mt-12 space-y-3 text-gray-600">
              <p className="text-lg">Complete Operations Manual</p>
              <p>Version 2.0 - Updated {new Date().toLocaleDateString()}</p>
            </div>
            
            <div className="mt-16 bg-gray-50 rounded-lg p-6">
              <h4 className="font-semibold text-lg mb-4">System Overview</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium">Retail Management</p>
                  <p className="text-gray-600">Product catalog, pricing, inventory tracking</p>
                </div>
                <div>
                  <p className="font-medium">Restaurant Distribution</p>
                  <p className="text-gray-600">Discount code login system, tiered pricing</p>
                </div>
                <div>
                  <p className="font-medium">Customer Management</p>
                  <p className="text-gray-600">Business accounts, rebate calculations</p>
                </div>
                <div>
                  <p className="font-medium">Analytics & Reports</p>
                  <p className="text-gray-600">Order tracking, sales data, exports</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table of Contents */}
        <Card className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-xl">Table of Contents</CardTitle>
          </CardHeader>
          <CardContent>
            <nav className="space-y-2">
              <a href="#overview" className="block text-blue-600 hover:underline">1. System Overview</a>
              <a href="#getting-started" className="block text-blue-600 hover:underline">2. Getting Started</a>
              <a href="#retail-products" className="block text-blue-600 hover:underline">3. Managing Retail Products</a>
              <a href="#restaurant-system" className="block text-blue-600 hover:underline">4. Restaurant Distribution System</a>
              <a href="#discount-codes" className="block text-blue-600 hover:underline">5. Discount Code Management</a>
              <a href="#restaurant-orders" className="block text-blue-600 hover:underline">6. Restaurant Order Processing</a>
              <a href="#customer-management" className="block text-blue-600 hover:underline">7. Customer Management</a>
              <a href="#rebates" className="block text-blue-600 hover:underline">8. Rebate System</a>
              <a href="#user-management" className="block text-blue-600 hover:underline">9. User Management</a>
              <a href="#reports" className="block text-blue-600 hover:underline">10. Reports & Analytics</a>
              <a href="#troubleshooting" className="block text-blue-600 hover:underline">11. Troubleshooting</a>
            </nav>
          </CardContent>
        </Card>

        {/* 1. System Overview */}
        <Card id="overview" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Info className="w-6 h-6" />
              1. System Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700 leading-relaxed">
              The Tops Supermarket Management System manages both retail operations and restaurant distribution through a unique dual-portal system. The restaurant portal uses discount codes as login credentials, eliminating traditional usernames and passwords.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Retail Management
                </h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Chinese grocery product catalog</li>
                  <li>• Real-time price management</li>
                  <li>• CSV import/export functionality</li>
                  <li>• Category-based organization</li>
                  <li>• Live price timestamp tracking</li>
                </ul>
              </div>
              
              <div className="border rounded-lg p-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Utensils className="w-4 h-4" />
                  Restaurant Distribution
                </h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Discount code login system</li>
                  <li>• 4-tier discount structure (0%-15%)</li>
                  <li>• Automatic best price application</li>
                  <li>• Bulk order management</li>
                  <li>• Mobile-optimized ordering</li>
                </ul>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-blue-600" />
                Key Innovation: Discount Code Login
              </h4>
              <p className="text-sm text-gray-700">
                Restaurants access the system using 4-character discount codes (e.g., "AB12") instead of traditional usernames and passwords. Each code provides access to specific discount tiers and automatically applies the best available pricing.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                Critical: Live Prices Timestamp
              </h4>
              <p className="text-sm text-gray-700">
                The glowing yellow "Live Prices" box shows when prices were last updated. This MUST be updated frequently to maintain customer trust and transparency.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 2. Getting Started */}
        <Card id="getting-started" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <CheckCircle className="w-6 h-6" />
              2. Getting Started
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="font-semibold text-lg">Admin Login</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Navigate to <code className="bg-gray-100 px-2 py-1 rounded">/admin</code></li>
              <li>Enter admin credentials: <code className="bg-gray-100 px-2 py-1 rounded">admin / admin123</code></li>
              <li>Access the full administrative dashboard</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">User Roles</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Badge className="bg-red-100 text-red-800 mt-1">Admin</Badge>
                <div>
                  <p className="font-medium">Full System Access</p>
                  <p className="text-sm text-gray-600">Manage all products, orders, users, and system settings</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Badge className="bg-blue-100 text-blue-800 mt-1">Order</Badge>
                <div>
                  <p className="font-medium">Order Management Only</p>
                  <p className="text-sm text-gray-600">View and process restaurant orders, limited editing access</p>
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Main Navigation Tabs</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-gray-500" />
                <span><strong>Products:</strong> Retail inventory management</span>
              </div>
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-gray-500" />
                <span><strong>Restaurant Orders:</strong> B2B order processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-gray-500" />
                <span><strong>Restaurant Products:</strong> Bulk product catalog</span>
              </div>
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-gray-500" />
                <span><strong>Discount Codes:</strong> Restaurant access management</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                <span><strong>Customers:</strong> Business account tracking</span>
              </div>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-500" />
                <span><strong>Rebates:</strong> Volume discount calculations</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 3. Managing Retail Products */}
        <Card id="retail-products" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Package className="w-6 h-6" />
              3. Managing Retail Products
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Retail products are displayed on the main customer-facing website with live pricing.
            </p>

            <h3 className="font-semibold text-lg">Product Categories</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>• Produce</div>
              <div>• Meat - Beef</div>
              <div>• Meat - Chicken</div>
              <div>• Meat - Pork</div>
              <div>• Meat - Hotpot</div>
              <div>• Seafood</div>
              <div>• Groceries</div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Adding Products</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Click <Badge className="bg-emerald-600 text-white">+ Add Product</Badge></li>
              <li>Fill required fields:
                <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                  <li>Product description (English)</li>
                  <li>Chinese name (optional but recommended)</li>
                  <li>Category selection</li>
                  <li>Size/weight information</li>
                  <li>Retail price and your cost price</li>
                </ul>
              </li>
              <li>Add image URL (optional but improves customer experience)</li>
              <li>Set active status and display order</li>
              <li>Save the product</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">Bulk Import/Export</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium flex items-center gap-2">
                <Upload className="w-4 h-4" />
                CSV Operations
              </h4>
              <ol className="list-decimal list-inside space-y-1 text-sm mt-2">
                <li>Click <Badge variant="outline">Import/Export</Badge> button</li>
                <li>Download template CSV with proper column headers</li>
                <li>Fill data following exact template format</li>
                <li>Upload completed CSV file</li>
                <li>Review validation results and fix any errors</li>
                <li>Confirm import to add products to catalog</li>
              </ol>
            </div>

            <h3 className="font-semibold text-lg mt-6">Price Update Protocol</h3>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-800">CRITICAL STEP</p>
                  <p className="text-sm text-gray-700 mt-1">
                    After ANY price changes, you MUST click the blue "Update Timestamp" button to update the "Live Prices" indicator. This shows customers when prices were last verified and maintains transparency.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 4. Restaurant Distribution System */}
        <Card id="restaurant-system" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Utensils className="w-6 h-6" />
              4. Restaurant Distribution System
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              The restaurant system operates as a separate B2B portal using discount codes for authentication and tiered pricing.
            </p>

            <h3 className="font-semibold text-lg">How Restaurant Access Works</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <ol className="list-decimal list-inside space-y-2 text-sm">
                <li><strong>No Username/Password:</strong> Restaurants use 4-character discount codes</li>
                <li><strong>Code as Login:</strong> Entering a valid code provides instant access</li>
                <li><strong>Auto-Pricing:</strong> System applies appropriate discounts automatically</li>
                <li><strong>Restaurant Info:</strong> Code validates and populates restaurant details</li>
              </ol>
            </div>

            <h3 className="font-semibold text-lg mt-6">Product Categories</h3>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-amber-600" />
                <span>Rice & Noodles</span>
              </div>
              <div className="flex items-center gap-2">
                <Utensils className="w-4 h-4 text-red-600" />
                <span>Proteins</span>
              </div>
              <div className="flex items-center gap-2">
                <Leaf className="w-4 h-4 text-green-600" />
                <span>Vegetables</span>
              </div>
              <div className="flex items-center gap-2">
                <Package className="w-4 h-4 text-orange-600" />
                <span>Sauces & Condiments</span>
              </div>
              <div className="flex items-center gap-2">
                <Snowflake className="w-4 h-4 text-blue-600" />
                <span>Dim Sum & Frozen</span>
              </div>
              <div className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-purple-600" />
                <span>Cooking Essentials</span>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Managing Restaurant Products</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Navigate to <Badge variant="outline">Restaurant Products</Badge></li>
              <li>Add products with:
                <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                  <li>Product name and detailed description</li>
                  <li>Category assignment with visual icons</li>
                  <li>Unit type (kg, lbs, case, each, box, bag)</li>
                  <li>Minimum order quantity enforcement</li>
                  <li>Base price per unit (before discounts)</li>
                  <li>Discount tier tags for eligibility</li>
                </ul>
              </li>
              <li>Products automatically appear in restaurant portal when active</li>
            </ol>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                Tier 0 Always Applied
              </h4>
              <p className="text-sm text-gray-700">
                All restaurant products automatically include Tier 0 (0% discount) as the baseline. Higher tiers provide additional savings on top of this base pricing.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* 5. Discount Code Management */}
        <Card id="discount-codes" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Tag className="w-6 h-6" />
              5. Discount Code Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Discount codes serve as both login credentials and pricing tier assignments for restaurants.
            </p>

            <h3 className="font-semibold text-lg">Discount Tier Structure</h3>
            <div className="space-y-2 mt-2">
              <div className="flex items-center justify-between p-3 bg-gray-100 rounded">
                <div>
                  <span className="font-medium">Tier 0 (Regular)</span>
                  <p className="text-sm text-gray-600">Always available • Base pricing</p>
                </div>
                <Badge className="bg-gray-200 text-gray-800">0% off</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                <div>
                  <span className="font-medium">Tier 1 (Bronze)</span>
                  <p className="text-sm text-gray-600">Standard restaurant discount</p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">7% off</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded">
                <div>
                  <span className="font-medium">Tier 2 (Silver)</span>
                  <p className="text-sm text-gray-600">Preferred restaurant discount</p>
                </div>
                <Badge className="bg-purple-100 text-purple-800">10% off</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-orange-50 rounded">
                <div>
                  <span className="font-medium">Tier 3 (Gold)</span>
                  <p className="text-sm text-gray-600">Premium restaurant discount</p>
                </div>
                <Badge className="bg-orange-100 text-orange-800">15% off</Badge>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Creating Discount Codes</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Go to <Badge variant="outline">Discount Codes</Badge> section</li>
              <li>Click <Badge className="bg-emerald-600 text-white">+ Add Code</Badge></li>
              <li>Enter 4-character alphanumeric code (auto-uppercased)</li>
              <li>Select discount tier (Tier 0-3)</li>
              <li>Enter complete restaurant information:
                <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                  <li>Restaurant business name</li>
                  <li>Contact person name</li>
                  <li>Email address for notifications</li>
                  <li>Phone number for order communication</li>
                  <li>Business address for deliveries</li>
                </ul>
              </li>
              <li>Set code status (active/inactive)</li>
              <li>Save to make code immediately available</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">Discount Logic</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium mb-2">Cascading Access System</h4>
              <ul className="text-sm space-y-1">
                <li>• <strong>Tier 3 customers:</strong> Access Tier 3, 2, 1, and 0 pricing</li>
                <li>• <strong>Tier 2 customers:</strong> Access Tier 2, 1, and 0 pricing</li>
                <li>• <strong>Tier 1 customers:</strong> Access Tier 1 and 0 pricing</li>
                <li>• <strong>Tier 0 customers:</strong> Access only Tier 0 (base) pricing</li>
                <li>• <strong>Best Price Applied:</strong> System automatically selects highest applicable discount</li>
              </ul>
            </div>

            <h3 className="font-semibold text-lg mt-6">Code Management Features</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>View usage statistics and total discount amounts given</li>
              <li>Export discount code data to CSV for record-keeping</li>
              <li>Deactivate codes without deleting restaurant data</li>
              <li>Track order history linked to each code</li>
              <li>Bulk operations for multiple code management</li>
            </ul>
          </CardContent>
        </Card>

        {/* 6. Restaurant Order Processing */}
        <Card id="restaurant-orders" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <ShoppingCart className="w-6 h-6" />
              6. Restaurant Order Processing
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Process and track bulk orders from restaurants with comprehensive order management tools.
            </p>

            <h3 className="font-semibold text-lg">Order Status Workflow</h3>
            <div className="flex items-center gap-2 text-sm mb-4">
              <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-blue-100 text-blue-800">Confirmed</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-orange-100 text-orange-800">Preparing</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-purple-100 text-purple-800">Ready</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-green-100 text-green-800">Delivered</Badge>
            </div>

            <h3 className="font-semibold text-lg">Order Management Features</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Advanced Filtering:</strong> By status, date range, restaurant, order amount</li>
              <li><strong>Quick Status Updates:</strong> Bulk operations for multiple orders</li>
              <li><strong>Order Details View:</strong> Complete customer info, items, pricing breakdown</li>
              <li><strong>Manual Status Override:</strong> Jump to any status when needed</li>
              <li><strong>Print/Export Options:</strong> Individual PDFs, bulk exports</li>
            </ul>

            <h3 className="font-semibold text-lg mt-6">Restaurant Filter Optimization</h3>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Smart Filtering:</strong> Restaurant dropdown shows only last year's active restaurants by default. Use "Find Restaurant" button to search historical data and access older restaurant records.
              </p>
            </div>

            <h3 className="font-semibold text-lg mt-6">Order Processing Steps</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>New orders appear as "Pending" status</li>
              <li>Review order details and customer information</li>
              <li>Confirm order to change status to "Confirmed"</li>
              <li>Update to "Preparing" when fulfillment begins</li>
              <li>Mark "Ready" when order is complete for pickup/delivery</li>
              <li>Final status "Delivered" when order reaches customer</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">Export and Reporting</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Export individual orders as PDF for customer records</li>
              <li>Bulk export filtered orders to CSV/Excel</li>
              <li>Advanced date range filtering for reporting periods</li>
              <li>Order analytics showing revenue and fulfillment metrics</li>
            </ul>
          </CardContent>
        </Card>

        {/* 7. Customer Management */}
        <Card id="customer-management" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Users className="w-6 h-6" />
              7. Customer Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Track business customers for rebate calculations and relationship management.
            </p>

            <h3 className="font-semibold text-lg">Adding Customers</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Click <Badge className="bg-emerald-600 text-white">+ Add Customer</Badge></li>
              <li>Enter complete business information:
                <ul className="list-disc list-inside ml-4 mt-1 text-sm">
                  <li>Business name and contact person</li>
                  <li>Email and phone number</li>
                  <li>Complete business address</li>
                  <li>Tax ID number (optional)</li>
                  <li>Business type and notes</li>
                </ul>
              </li>
              <li>Save customer record for order linking</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">Customer Analytics</h3>
            <p className="text-gray-700">Each customer record tracks:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-700 mt-2">
              <li>Complete purchase history with order details</li>
              <li>Annual purchase totals for rebate calculations</li>
              <li>Rebate tier qualification status</li>
              <li>Payment and order frequency patterns</li>
              <li>Customer lifetime value metrics</li>
            </ul>

            <h3 className="font-semibold text-lg mt-6">Linking Orders to Customers</h3>
            <p className="text-gray-700">
              Restaurant orders can be linked to customer records to automatically track purchase volumes for rebate calculations and customer relationship management.
            </p>
          </CardContent>
        </Card>

        {/* 8. Rebate System */}
        <Card id="rebates" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <TrendingUp className="w-6 h-6" />
              8. Rebate System
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Automated volume-based rebate calculations for qualifying business customers.
            </p>

            <h3 className="font-semibold text-lg">Rebate Tier Structure</h3>
            <div className="space-y-2 mt-2">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded">
                <div>
                  <span className="font-medium">Bronze Tier</span>
                  <p className="text-sm text-gray-600">Annual purchases ≥ $25,000</p>
                </div>
                <Badge className="bg-amber-100 text-amber-800">1% rebate</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div>
                  <span className="font-medium">Silver Tier</span>
                  <p className="text-sm text-gray-600">Annual purchases ≥ $50,000</p>
                </div>
                <Badge className="bg-gray-100 text-gray-800">2% rebate</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded">
                <div>
                  <span className="font-medium">Gold Tier</span>
                  <p className="text-sm text-gray-600">Annual purchases ≥ $100,000</p>
                </div>
                <Badge className="bg-yellow-100 text-yellow-800">3% rebate</Badge>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Automatic Rebate Calculation</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>System tracks all linked customer orders automatically</li>
              <li>Annual purchase totals calculated in real-time</li>
              <li>Tier qualification determined by purchase thresholds</li>
              <li>Rebate amounts calculated automatically</li>
              <li>Rebate records created for approval workflow</li>
            </ol>

            <h3 className="font-semibold text-lg mt-6">Rebate Workflow</h3>
            <div className="flex items-center gap-2 text-sm mb-4">
              <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-blue-100 text-blue-800">Approved</Badge>
              <ArrowRight className="w-4 h-4" />
              <Badge className="bg-green-100 text-green-800">Paid</Badge>
            </div>

            <h3 className="font-semibold text-lg mt-6">Rebate Management</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Review and approve pending rebate calculations</li>
              <li>Export rebate reports for accounting purposes</li>
              <li>Track rebate payment status and history</li>
              <li>Generate year-end rebate reconciliation reports</li>
              <li>Adjust rebate tiers and thresholds as needed</li>
            </ul>
          </CardContent>
        </Card>

        {/* 9. User Management */}
        <Card id="user-management" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6" />
              9. User Management
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Manage system users and access levels. Available only to administrators.
            </p>

            <h3 className="font-semibold text-lg">Default System Users</h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 bg-red-50 rounded">
                <div>
                  <span className="font-medium">admin</span>
                  <p className="text-sm text-gray-600">Full system administrator</p>
                </div>
                <Badge className="bg-red-100 text-red-800">Admin Access</Badge>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded">
                <div>
                  <span className="font-medium">order</span>
                  <p className="text-sm text-gray-600">Order processing specialist</p>
                </div>
                <Badge className="bg-blue-100 text-blue-800">Order Access</Badge>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Managing Users</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-700">
              <li>Access User Management (Admin only)</li>
              <li>Add new users with appropriate role assignment</li>
              <li>Set strong passwords and enforce regular changes</li>
              <li>Monitor user activity and access patterns</li>
              <li>Deactivate users when access is no longer needed</li>
            </ol>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <h4 className="font-semibold flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                Security Best Practices
              </h4>
              <ul className="text-sm space-y-1">
                <li>• Change default passwords immediately</li>
                <li>• Use strong, unique passwords for each user</li>
                <li>• Never share login credentials between staff</li>
                <li>• Review user access regularly</li>
                <li>• Monitor system activity logs</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* 10. Reports & Analytics */}
        <Card id="reports" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <BarChart3 className="w-6 h-6" />
              10. Reports & Analytics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Comprehensive reporting and analytics for business intelligence and operations management.
            </p>

            <h3 className="font-semibold text-lg">Available Reports</h3>
            <div className="space-y-3 mt-2">
              <div className="border rounded-lg p-3">
                <h4 className="font-medium flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" />
                  Restaurant Order Analytics
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Order volume, revenue trends, fulfillment rates, average order values by time period and customer segment.
                </p>
              </div>
              
              <div className="border rounded-lg p-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Discount Code Performance
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Usage statistics, discount amounts given, tier utilization, and customer acquisition through codes.
                </p>
              </div>
              
              <div className="border rounded-lg p-3">
                <h4 className="font-medium flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Product Performance Analysis
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Best-selling products, category performance, pricing analysis, and inventory turnover rates.
                </p>
              </div>

              <div className="border rounded-lg p-3">
                <h4 className="font-medium flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Customer & Rebate Reports
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Customer lifetime value, rebate calculations, tier qualifications, and payment tracking.
                </p>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">Export Capabilities</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>CSV exports for all data tables with current filters applied</li>
              <li>Excel format exports for complex data analysis</li>
              <li>PDF exports for order confirmations and customer records</li>
              <li>Scheduled report generation for regular business reviews</li>
              <li>Custom date range filtering for specific reporting periods</li>
            </ul>

            <h3 className="font-semibold text-lg mt-6">Business Intelligence Features</h3>
            <ul className="list-disc list-inside space-y-1 text-gray-700">
              <li>Real-time dashboard with key performance indicators</li>
              <li>Trend analysis for revenue and order patterns</li>
              <li>Customer segmentation and behavior analysis</li>
              <li>Discount effectiveness and ROI tracking</li>
              <li>Seasonal and periodic performance comparisons</li>
            </ul>
          </CardContent>
        </Card>

        {/* 11. Troubleshooting */}
        <Card id="troubleshooting" className="print:shadow-none print:border-2">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              11. Troubleshooting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <h3 className="font-semibold text-lg">Common Issues & Solutions</h3>
            
            <div className="space-y-4 mt-4">
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600">Restaurant can't access system with discount code</h4>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Verify code is exactly 4 characters and active in system</li>
                  <li>Check code hasn't been deactivated or deleted</li>
                  <li>Ensure code is properly linked to restaurant information</li>
                  <li>Test code entry (system auto-uppercases)</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600">Discounts not applying correctly</h4>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Check products have proper discount tier tags assigned</li>
                  <li>Verify restaurant's discount tier in code settings</li>
                  <li>Confirm Tier 0 is always available (baseline pricing)</li>
                  <li>Test cascading discount logic (higher tiers access lower)</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600">Orders not saving or updating</h4>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Check internet connection stability</li>
                  <li>Verify user has appropriate permissions</li>
                  <li>Clear browser cache and reload page</li>
                  <li>Check for any error messages in browser console</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600">Live Prices timestamp not updating</h4>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Ensure you clicked the blue "Update Timestamp" button</li>
                  <li>Check admin permissions for timestamp updates</li>
                  <li>Refresh the customer-facing page to see changes</li>
                  <li>Verify system time settings are correct</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600">Restaurant product deletions failing</h4>
                <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                  <li>Check if product is referenced in existing orders</li>
                  <li>Remove discount tag assignments before deletion</li>
                  <li>Deactivate product instead of deleting if needed</li>
                  <li>Contact system administrator for complex deletions</li>
                </ul>
              </div>
            </div>

            <h3 className="font-semibold text-lg mt-6">System Maintenance Best Practices</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li><strong>Regular Backups:</strong> Export critical data weekly (products, customers, orders)</li>
              <li><strong>Price Timestamp:</strong> Update immediately after any price changes</li>
              <li><strong>Security Updates:</strong> Change passwords every 90 days</li>
              <li><strong>Data Accuracy:</strong> Double-check all entries before saving</li>
              <li><strong>Communication:</strong> Notify restaurants before making discount code changes</li>
              <li><strong>Monitoring:</strong> Check system performance and error logs regularly</li>
            </ul>

            <h3 className="font-semibold text-lg mt-6">Emergency Procedures</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-red-600" />
                System Down or Critical Issues
              </h4>
              <ol className="text-sm list-decimal list-inside space-y-1">
                <li>Check internet connection and power supply</li>
                <li>Try accessing from different device/browser</li>
                <li>Document error messages and reproduction steps</li>
                <li>Contact system administrator immediately</li>
                <li>Implement manual order processing backup if needed</li>
              </ol>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <h4 className="font-medium flex items-center gap-2 mb-2">
                <Phone className="w-4 h-4" />
                Support Resources
              </h4>
              <p className="text-sm">
                For issues not covered in this guide, contact your system administrator or IT support team. Keep this guide accessible for reference during troubleshooting sessions.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm py-8 print:hidden">
          <p>Tops Supermarket Management System - Administrator Guide</p>
          <p>Version 2.0 - Updated {new Date().toLocaleDateString()}</p>
          <p>Comprehensive guide covering retail management and restaurant distribution systems</p>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          @page {
            size: letter;
            margin: 0.75in;
          }
          
          /* Ensure cards don't break across pages */
          .card, [class*="Card"] {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Add page breaks before major sections */
          #overview, #getting-started, #retail-products, 
          #restaurant-system, #discount-codes, #restaurant-orders,
          #customer-management, #rebates, #user-management, 
          #reports, #troubleshooting {
            page-break-before: always;
            break-before: page;
          }
          
          /* Except for the first section after title page */
          #overview {
            page-break-before: auto;
          }
          
          /* Title page always starts on new page */
          .print\\:break-before-page {
            page-break-before: always !important;
            break-before: page !important;
          }
          
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          
          .print\\:border-2 {
            border-width: 2px !important;
          }
          
          .print\\:hidden {
            display: none !important;
          }
          
          /* Better text rendering for print */
          body {
            font-size: 11pt;
            line-height: 1.5;
          }
          
          h1 { font-size: 24pt; }
          h2 { font-size: 20pt; }
          h3 { font-size: 16pt; }
          h4 { font-size: 14pt; }
          
          /* Ensure links are readable in print */
          a {
            color: black !important;
            text-decoration: none !important;
          }
          
          /* Code blocks should be visible */
          code {
            background-color: #f0f0f0 !important;
            border: 1px solid #ddd !important;
            padding: 2px 4px !important;
          }
          
          /* Background colors for badges and alerts */
          .bg-amber-50, .bg-blue-50, .bg-yellow-50, .bg-gray-50, 
          .bg-green-50, .bg-red-50, .bg-purple-50, .bg-orange-50 {
            background-color: #f9f9f9 !important;
            border: 1px solid #ddd !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Ensure badges are readable */
          [class*="Badge"] {
            border: 1px solid currentColor !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          /* Keep content together */
          li {
            page-break-inside: avoid;
            break-inside: avoid;
          }
          
          /* Prevent orphaned headings */
          h1, h2, h3, h4, h5, h6 {
            page-break-after: avoid;
            break-after: avoid;
          }
          
          /* Keep related content together */
          .space-y-2 > *, .space-y-3 > *, .space-y-4 > * {
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}